import {debounce} from 'lodash-es';
import {useCallback, useMemo, useRef, useState} from 'react';

type SearchBarProps = {
  handleSearch: (searchTerm: string) => void;
  handleAutoComplete?: (searchTerm: string) => void;
  initialSearchTerm?: string;
};
// Both handlers declare only the slice of the DOM event they read, so the
// clear button (which calls handleSearchTermChange with a `{target: {value}}`
// stub) stays type-safe while a real React event still satisfies them.
type SearchTermChangeEvent = {target: {value: string}};
type SearchKeyDownEvent = Pick<
  React.KeyboardEvent<HTMLInputElement>,
  'code' | 'preventDefault'
>;

type SearchBarReturnProps = {
  handleSearchTermChange: (event: SearchTermChangeEvent) => void;
  handleKeyDown: (event: SearchKeyDownEvent) => void;
  searchTerm: string;
  //kills a queued autocomplete before it fires
  cancelPendingAutoComplete: () => void;
};
const useSearchBar = ({
  handleSearch,
  handleAutoComplete,
  initialSearchTerm = '',
}: SearchBarProps): SearchBarReturnProps => {
  const [searchTerm, setSearchTerm] = useState<string>(initialSearchTerm);

  // The last term this box sent through handleAutoComplete. It comes back as
  // initialSearchTerm after the debounce, by which time the user may have typed
  // on, so it must not overwrite the box.
  const lastAutoCompletedRef = useRef<string | null>(null);

  // A search kept in the URL can change from outside the box (a link, a
  // cleared filter). Follow it, unless the box already holds the same text or
  // the box itself just sent it.
  const [seenInitialSearchTerm, setSeenInitialSearchTerm] =
    useState(initialSearchTerm);
  if (seenInitialSearchTerm !== initialSearchTerm) {
    setSeenInitialSearchTerm(initialSearchTerm);
    if (
      initialSearchTerm !== lastAutoCompletedRef.current &&
      searchTerm.trim() !== initialSearchTerm
    ) {
      lastAutoCompletedRef.current = null;
      setSearchTerm(initialSearchTerm);
    }
  }

  // Keep a stable ref so the debounced fn below never goes stale
  const handleAutoCompleteRef = useRef(handleAutoComplete);
  handleAutoCompleteRef.current = handleAutoComplete;

  // Single stable debounced function — created once for the lifetime of the hook
  const debouncedAutoComplete = useMemo(
    () =>
      debounce((val: string) => {
        lastAutoCompletedRef.current = val.trim();
        handleAutoCompleteRef.current?.(val);
      }, 500),
    [],
  );

  //drop a queued autocomplete. also clears lastAutoCompleted so the dropped
  //term cannot block a later URL-driven update of the box
  const cancelPendingAutoComplete = useCallback(() => {
    lastAutoCompletedRef.current = null;
    debouncedAutoComplete.cancel();
  }, [debouncedAutoComplete]);

  const handleSearchTermChange = useCallback(
    (event: SearchTermChangeEvent) => {
      const val = event.target.value;
      setSearchTerm(val);
      if (handleAutoComplete && val.length > 2) {
        debouncedAutoComplete(val);
      } else {
        //too short to autocomplete, drop what the last keystroke queued
        debouncedAutoComplete.cancel();
      }
    },
    [handleAutoComplete, debouncedAutoComplete],
  );

  // Scoped to the search input (not a global document listener) so Enter only triggers
  // a search when the input itself is focused — pressing Enter elsewhere (e.g. inside an
  // open modal) no longer fires a search request.
  const handleKeyDown = useCallback(
    (event: SearchKeyDownEvent) => {
      // `code` alone covers both Enter keys; the legacy charCode/keyCode === 13
      // fallbacks it replaces are deprecated and were redundant with it.
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        event.preventDefault();
        handleSearch(searchTerm);
      }
    },
    [handleSearch, searchTerm],
  );

  return {
    handleSearchTermChange,
    handleKeyDown,
    searchTerm,
    cancelPendingAutoComplete,
  };
};

export default useSearchBar;
